import { Subject } from 'rxjs';
import type { components } from '@/generated/contracts/api-v1';
import { keepLocalCopy } from '@/utils/localCopies';
import { binary, multipart, toResult, v1, type V1Result } from './client';
import { ApiProblem } from './problem';

/**
 * Every file the app stores goes straight to storage near the user, never through the backend: createUpload signs a
 * PUT, the file is sent to it, and completeUpload checks it and gives it its public address. A deployment still on
 * Supabase Storage answers `direct_uploads_unavailable`, and the file goes through the backend with putStorageObject,
 * as it always has, so nothing changes until the release turns direct uploads on.
 *
 * A file is never stored under the name asked for: storage puts a version in it, so the caller keeps the path or
 * address the upload answers and never builds one from the name. The creator's own new image or video is then shown from
 * the file already in the browser (localCopies.ts), so nothing is downloaded back.
 *
 * An asset type's files (models above all, up to 512 MB) go the same way into the type's own folder
 * (uploadAssetTypeFile), and uploadAssetType or putAssetFile then records the completed upload in place of the bytes.
 */

type UploadDto = components['schemas']['UploadDto'];
type CreateUploadRequest = components['schemas']['CreateUploadRequest'];

/** Where createUpload puts a file: a bucket and path putStorageObject allows, or an asset type's folder and the file's name. */
type UploadTarget = Pick<CreateUploadRequest, 'bucket' | 'path' | 'assetTypeUniqueId'>;

/** A stored file, as its upload answered it. */
export interface UploadedFile {
  bucket: string;
  path: string;
  /** The file's public address: save this. */
  url: string;
  /** For an image sent straight to storage, its thumbnail (JPEG, `<name>.w1152.jpg`, at most 1152 px wide); null otherwise. */
  thumbnailUrl: string | null;
}

/** A file of an asset type sent straight to storage, into the type's folder. */
export interface UploadedAssetFile extends UploadedFile {
  /** What uploadAssetType and putAssetFile take, as `uploadUniqueId`, in place of the file. */
  uploadUniqueId: string;
}

/** An upload the server refused for good: told once to the person (ServiceMonitorBoot), never tried again. */
export interface UploadRefusal {
  /** The file's name, as asked for. */
  name: string;
  problem: ApiProblem;
}

export const uploadRefused$ = new Subject<UploadRefusal>();

/** Tries per upload when the answer may pass (no answer, a server error, the rate limit, an expired URL). */
const ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 10_000;

/** Off once the deployment says it takes no direct uploads: it won't until a release, which reloads the page. */
let directUploads = true;

const MEDIA_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  glb: 'model/gltf-binary',
  json: 'application/json',
};

/** The type the file is stored with: the caller's, else the file's own, else its extension's. */
function mediaTypeOf(file: Blob, path: string, contentType?: string): string {
  const given = contentType?.trim() || file.type;
  if (given) return given;
  const extension = path.split('.').pop()?.toLowerCase() ?? '';
  return MEDIA_TYPES[extension] ?? 'application/octet-stream';
}

/** An answer that may pass if the same step is tried again. */
const mayPass = (problem: ApiProblem) =>
  problem.status === 0 || problem.status === 408 || problem.status === 429 || problem.code === 'upload_missing' || problem.code === 'upload_url_refused'
  || (problem.status >= 500 && problem.code !== 'direct_uploads_unavailable');

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

function retryDelay(failures: number, problem: ApiProblem): number {
  const backoff = RETRY_BASE_DELAY_MS * 2 ** (failures - 1) + Math.random() * 250;
  return Math.min(RETRY_MAX_DELAY_MS, Math.max(backoff, (problem.retryAfterSeconds ?? 0) * 1000));
}

/** Sends the file to the URL createUpload signed, with exactly the headers it was signed with. */
async function sendTo(upload: UploadDto, file: Blob, signal?: AbortSignal): Promise<ApiProblem | null> {
  try {
    const response = await fetch(upload.url, { method: upload.method || 'PUT', headers: upload.headers, body: file, signal });
    if (response.ok) return null;
    // Storage refuses a URL past its time with 403; a repeat of createUpload signs a fresh one
    return new ApiProblem({
      status: response.status,
      code: response.status === 403 ? 'upload_url_refused' : `storage_${response.status}`,
      title: response.status >= 500 ? 'Storage could not take the file. Please try again in a moment.' : 'Storage refused the file.',
    });
  } catch (error) {
    return ApiProblem.fromError(error);
  }
}

/**
 * createUpload, the PUT, then completeUpload, each tried again while its answer may pass. All three are safe to
 * repeat: the upload keeps the id it was made with. A cancelled upload stops at once.
 */
async function sendDirect(target: UploadTarget, file: Blob, contentType: string, signal?: AbortSignal): Promise<V1Result<UploadedAssetFile>> {
  const uniqueId = crypto.randomUUID();
  let upload: UploadDto | null = null;
  let sent = false;
  let failures = 0;
  for (;;) {
    let problem: ApiProblem | null;
    if (!upload) {
      const created = await toResult(v1.POST('/api/v1/uploads', { body: { uniqueId, ...target, contentType, size: file.size }, signal }));
      upload = created.data;
      problem = created.error;
    } else if (!sent) {
      problem = await sendTo(upload, file, signal);
      sent = !problem;
      if (problem?.code === 'upload_url_refused') upload = null;
    } else {
      const completed = await toResult(v1.POST('/api/v1/uploads/{uploadUniqueId}/complete', {
        params: { path: { uploadUniqueId: upload.uploadUniqueId } },
        signal,
      }));
      if (completed.data) return { data: { ...completed.data, uploadUniqueId: upload.uploadUniqueId }, error: null };
      problem = completed.error;
      // What arrived wasn't the file signed for, or nothing did: it is sent again, to a fresh URL
      if (problem.code === 'upload_missing') {
        upload = null;
        sent = false;
      }
    }
    if (!problem) continue;
    failures += 1;
    if (signal?.aborted || !mayPass(problem) || failures >= ATTEMPTS) return { data: null, error: problem };
    await wait(retryDelay(failures, problem));
  }
}

/** Today's way: the file through the backend with putStorageObject, which answers the path it stored it at. */
async function sendThroughBackend(bucket: string, path: string, file: Blob): Promise<V1Result<UploadedFile>> {
  const stored = await toResult(v1.PUT('/api/v1/storage-objects', {
    body: { file: binary(file), bucket, path },
    bodySerializer: multipart,
  }));
  if (stored.error) return { data: null, error: stored.error };
  return { data: { ...stored.data, thumbnailUrl: null }, error: null };
}

/**
 * Stores `file` in `bucket`, asking for `path`, and answers where it was stored. `contentType` overrides the file's own
 * type. A refusal from direct storage is told to the person (uploadRefused$) and not tried again.
 */
export async function uploadFile(bucket: string, path: string, file: Blob, contentType?: string): Promise<V1Result<UploadedFile>> {
  const type = mediaTypeOf(file, path, contentType);
  // The stored type is the body's: an explicit one re-types the bytes
  const body = type !== file.type ? new Blob([file], { type }) : file;

  let result: V1Result<UploadedFile> | null = null;
  // An empty file can't be signed for, so it goes the old way
  if (directUploads && body.size > 0) {
    const direct = await sendDirect({ bucket, path }, body, type);
    if (direct.error?.code === 'direct_uploads_unavailable') {
      directUploads = false;
    } else if (direct.error) {
      if (!mayPass(direct.error)) uploadRefused$.next({ name: path.split('/').pop() || path, problem: direct.error });
      result = { data: null, error: direct.error };
    } else {
      // The upload's id only matters to an asset type's records: a stored file answers where it is and nothing more
      const { bucket: storedIn, path: storedAt, url, thumbnailUrl } = direct.data;
      result = { data: { bucket: storedIn, path: storedAt, url, thumbnailUrl }, error: null };
    }
  }
  result ??= await sendThroughBackend(bucket, path, body);

  if (result.data) keepLocalCopy(result.data.url, body, type);
  return result;
}

/**
 * Sends a file of an asset type (a model, its source image, a converted file) straight to storage, into the type's
 * folder under `fileName`; uploadAssetType or putAssetFile then records it by its `uploadUniqueId`. `assetTypeId` may
 * be the id a type is about to be created with. Storage types the file by its name, as the backend does.
 *
 * Answers null when the deployment takes no direct uploads (or the file is empty): the caller sends the file through
 * the backend instead, as it always has. A refusal comes back for the caller to report, as its multipart one would.
 */
export async function uploadAssetTypeFile(
  assetTypeId: string,
  fileName: string,
  file: Blob,
  signal?: AbortSignal,
): Promise<V1Result<UploadedAssetFile> | null> {
  if (!directUploads || file.size === 0) return null;
  const type = mediaTypeOf(file, fileName);
  const result = await sendDirect({ assetTypeUniqueId: assetTypeId, path: fileName }, file, type, signal);
  if (result.error?.code === 'direct_uploads_unavailable') {
    directUploads = false;
    return null;
  }
  // Only an image or a video, which the web app may show; a model or a sidecar is only ever loaded by the renderer, from storage
  if (result.data) keepLocalCopy(result.data.url, file, type);
  return result;
}
